import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { AYRSHIRE_TOWNS, BUDGET_CAP } from "@/lib/constants";
import { Trash2, Send, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  address_line: "",
  town: "",
  postcode: "",
  notes: "",
};

export const SubmissionForm = ({
  open,
  onOpenChange,
  selectedServices,
  onRemoveService,
  total,
  onSuccess,
}) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const over = total > BUDGET_CAP;

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2)
      e.full_name = "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Enter a valid email address.";
    if (!form.phone.trim() || form.phone.trim().length < 5)
      e.phone = "Enter a contact phone number.";
    if (!form.address_line.trim() || form.address_line.trim().length < 3)
      e.address_line = "Enter your street address.";
    if (!form.town.trim()) e.town = "Choose an Ayrshire town.";
    if (!form.postcode.trim() || form.postcode.trim().length < 3)
      e.postcode = "Enter your postcode.";
    if (selectedServices.length === 0)
      e.services = "Select at least one service.";
    if (over)
      e.budget = `Your selection exceeds the £${BUDGET_CAP.toLocaleString()} budget.`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        service_ids: selectedServices.map((s) => s.id),
      };
      const res = await axios.post(`${API}/submissions`, payload);
      setDone(res.data);
      toast.success("Quote request sent! We'll be in touch shortly.");
      onSuccess?.();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err.message || "Submission failed";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(initialForm);
    setErrors({});
    setDone(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent
        data-testid="submission-dialog"
        className="max-w-3xl border-2 border-black rounded-none p-0 gap-0 bg-white max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader className="border-b-2 border-black p-5 md:p-8">
          <DialogTitle className="font-display text-2xl font-black tracking-tight text-black md:text-3xl">
            {done ? "Quote request sent" : "Your garden makeover"}
          </DialogTitle>
          <DialogDescription className="mt-1 font-display text-xs font-bold uppercase tracking-[0.25em] text-black/60">
            {done
              ? "We'll be in touch"
              : "Review your list — send to Home Guard Developments"}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <SuccessPanel
            data={done}
            onClose={() => {
              reset();
              onOpenChange(false);
            }}
          />
        ) : (
          <form onSubmit={handleSubmit} className="p-5 md:p-8">
            {/* Selected items recap */}
            <div className="mb-7 border-2 border-black">
              <div className="flex items-center justify-between border-b-2 border-black bg-[#0A0A0A] px-4 py-3">
                <span className="font-display text-[10px] font-bold uppercase tracking-[0.25em] text-white">
                  Selected Services ({selectedServices.length})
                </span>
                <span
                  data-testid="dialog-total"
                  className={`font-display text-base font-black tracking-tight ${
                    over ? "text-[#EF4444]" : "text-[#9D4CDD]"
                  }`}
                >
                  £{total.toLocaleString()} / £{BUDGET_CAP.toLocaleString()}
                </span>
              </div>
              {selectedServices.length === 0 ? (
                <div className="p-4 font-display text-sm text-black/60">
                  No services selected yet.
                </div>
              ) : (
                <ul>
                  {selectedServices.map((s, i) => (
                    <li
                      key={s.id}
                      data-testid={`dialog-item-${s.id}`}
                      className={`flex items-center justify-between gap-3 px-4 py-3 ${
                        i < selectedServices.length - 1
                          ? "border-b border-black/10"
                          : ""
                      }`}
                    >
                      <span className="font-display text-sm font-medium text-black">
                        {s.name}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="font-display text-sm font-bold text-black">
                          £{s.price.toLocaleString()}
                        </span>
                        <button
                          type="button"
                          data-testid={`dialog-remove-${s.id}`}
                          aria-label={`Remove ${s.name}`}
                          onClick={() => onRemoveService(s)}
                          className="inline-flex h-7 w-7 items-center justify-center border border-black text-black hover:bg-[#EF4444] hover:text-white hover:border-[#EF4444]"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {errors.services && (
              <ErrorBox testid="error-services">{errors.services}</ErrorBox>
            )}
            {errors.budget && (
              <ErrorBox testid="error-budget">{errors.budget}</ErrorBox>
            )}

            {/* User details */}
            <h3 className="mb-4 font-display text-[10px] font-bold uppercase tracking-[0.25em] text-black">
              Your Details
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field
                label="Full name"
                testid="input-full-name"
                value={form.full_name}
                onChange={(v) => update("full_name", v)}
                error={errors.full_name}
                placeholder="Jane MacDonald"
              />
              <Field
                label="Email"
                testid="input-email"
                type="email"
                value={form.email}
                onChange={(v) => update("email", v)}
                error={errors.email}
                placeholder="jane@example.co.uk"
              />
              <Field
                label="Phone"
                testid="input-phone"
                value={form.phone}
                onChange={(v) => update("phone", v)}
                error={errors.phone}
                placeholder="07700 900123"
              />
              <Field
                label="Postcode"
                testid="input-postcode"
                value={form.postcode}
                onChange={(v) => update("postcode", v.toUpperCase())}
                error={errors.postcode}
                placeholder="KA7 1AA"
              />
              <div className="md:col-span-2">
                <Field
                  label="Street address"
                  testid="input-address"
                  value={form.address_line}
                  onChange={(v) => update("address_line", v)}
                  error={errors.address_line}
                  placeholder="12 High Street"
                />
              </div>
              <div>
                <Label>Ayrshire town</Label>
                <Select
                  value={form.town}
                  onValueChange={(v) => update("town", v)}
                >
                  <SelectTrigger
                    data-testid="input-town"
                    className="rounded-none border-2 border-black h-12 font-display text-sm"
                  >
                    <SelectValue placeholder="Select your town" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-2 border-black">
                    {AYRSHIRE_TOWNS.map((t) => (
                      <SelectItem
                        key={t}
                        value={t}
                        data-testid={`town-option-${t.replace(/\s+/g, "-").toLowerCase()}`}
                        className="rounded-none font-display"
                      >
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.town && (
                  <FieldError testid="error-town">{errors.town}</FieldError>
                )}
              </div>
              <div className="md:col-span-2">
                <Label>Anything we should know? (optional)</Label>
                <textarea
                  data-testid="input-notes"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Access, dates, gate width, etc."
                  className="w-full border-2 border-black bg-white p-3 font-display text-sm text-black placeholder:text-black/40 focus:outline-none focus:ring-2 focus:ring-[#9D4CDD] focus:ring-offset-0"
                />
              </div>
            </div>

            <div className="mt-8 flex flex-col-reverse gap-3 border-t-2 border-black pt-6 sm:flex-row sm:justify-end">
              <button
                type="button"
                data-testid="dialog-cancel"
                onClick={() => onOpenChange(false)}
                className="border-2 border-black bg-white px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-black hover:text-white"
              >
                Keep editing
              </button>
              <button
                type="submit"
                data-testid="dialog-submit"
                disabled={submitting || over || selectedServices.length === 0}
                className={`inline-flex items-center justify-center gap-2 border-2 px-7 py-3 font-display text-xs font-bold uppercase tracking-[0.2em] transition-all ${
                  submitting || over || selectedServices.length === 0
                    ? "cursor-not-allowed border-black/20 bg-black/10 text-black/40"
                    : "border-black bg-[#9D4CDD] text-white hover:bg-black hover:border-black"
                }`}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" /> Send to Home Guard
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, value, onChange, error, type = "text", placeholder, testid }) => (
  <div>
    <Label>{label}</Label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`h-12 w-full border-2 bg-white px-3 font-display text-sm text-black placeholder:text-black/40 focus:outline-none focus:ring-2 focus:ring-[#9D4CDD] focus:ring-offset-0 ${
        error ? "border-[#EF4444]" : "border-black"
      }`}
    />
    {error && <FieldError testid={`error-${testid}`}>{error}</FieldError>}
  </div>
);

const Label = ({ children }) => (
  <label className="mb-1.5 block font-display text-[10px] font-bold uppercase tracking-[0.25em] text-black">
    {children}
  </label>
);

const FieldError = ({ children, testid }) => (
  <div
    data-testid={testid}
    className="mt-1 inline-flex items-center gap-1 font-display text-xs font-medium text-[#EF4444]"
  >
    <AlertCircle className="h-3 w-3" />
    {children}
  </div>
);

const ErrorBox = ({ children, testid }) => (
  <div
    data-testid={testid}
    className="mb-5 flex items-start gap-2 border-2 border-[#EF4444] bg-[#EF4444]/5 p-3 font-display text-sm text-[#EF4444]"
  >
    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
    {children}
  </div>
);

const SuccessPanel = ({ data, onClose }) => (
  <div data-testid="success-panel" className="p-6 md:p-10">
    <div className="mx-auto max-w-md text-center">
      <div className="mx-auto mb-5 inline-flex h-16 w-16 items-center justify-center border-2 border-black bg-[#9D4CDD]">
        <CheckCircle2 className="h-9 w-9 text-white" strokeWidth={2.5} />
      </div>
      <h3 className="font-display text-3xl font-black tracking-tight text-black">
        On its way!
      </h3>
      <p className="mt-3 font-display text-base text-black/70">
        We've recorded your request (#{data.id.slice(0, 8)}) totalling{" "}
        <strong className="text-black">
          £{data.total_cost.toLocaleString()}
        </strong>
        . The team at Home Guard Developments will email or call you within 1
        working day.
      </p>
      {!data.email_sent && (
        <p className="mt-4 border-2 border-black bg-[#F4F4F5] p-3 font-display text-xs text-black/70">
          Note: your details were saved successfully. Email delivery is pending
          confirmation by Home Guard.
        </p>
      )}
      <button
        type="button"
        data-testid="success-close"
        onClick={onClose}
        className="mt-7 border-2 border-black bg-black px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#9D4CDD] hover:border-[#9D4CDD]"
      >
        Close
      </button>
    </div>
  </div>
);
